/**
 * `PendingAgencyRequestsCard` — ops2026 Agencies panel header.
 *
 * ADR-0263 §8. Lists pending self-serve agency-plan requests
 * (`agency_request.status = 'pending'`) with account, plan, interval,
 * requested-at and a Dismiss action. The staff flip of the request
 * from pending → approved is NOT a shortcut here: it's a side effect
 * of the existing manual flow (turn `agency` feature on in the Agency
 * Feature card + assign the plan in Account → Plans). A separate
 * "Approve" here would create two sources of truth for what approved
 * means. Hidden entirely when the list is empty (staff never see an
 * always-empty section).
 */

import { ExternalLink, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import type { AdminAgencyRequestRow } from '@sill/types/api';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { Card } from '../../components/atoms/Card.js';
import { formatIsoCompact } from '../../lib/format.js';
import { useC } from '../../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

export interface PendingAgencyRequestsCardProps {
  /** Called after a successful dismiss so the parent can re-run
   *  `loadAgencies` (a dismissed account may surface as a new agency
   *  if staff approve + flip agency ON). Fire-and-forget on the
   *  parent's side. */
  readonly onChanged?: () => void;
}

export function PendingAgencyRequestsCard(
  props: PendingAgencyRequestsCardProps,
): JSX.Element | null {
  const C = useC();
  const api = useAdminApi();

  const [rows, setRows] = useState<readonly AdminAgencyRequestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dismissing, setDismissing] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.listAgencyRequests();
      setRows(res.requests);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load requests.');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const dismiss = useCallback(
    async (account_id: string): Promise<void> => {
      if (dismissing !== null) return;
      setDismissing(account_id);
      setError(null);
      try {
        await api.dismissAgencyRequest(account_id);
        setRows((cur) => cur.filter((r) => r.account_id !== account_id));
        props.onChanged?.();
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Could not dismiss.');
      } finally {
        setDismissing(null);
      }
    },
    [api, dismissing, props],
  );

  // Hidden when the list is empty. A loading flicker is fine — the
  // list is admin-side and almost always zero.
  if (loading) return null;
  if (error === null && rows.length === 0) return null;

  return (
    <Card style={{ padding: 16, marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
        <div>
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              letterSpacing: 1.5,
              color: C.limeFg,
              textTransform: 'uppercase',
              marginBottom: 4,
            }}
          >
            Pending agency-plan requests
          </div>
          <div style={{ fontFamily: serifFont, fontSize: 20, color: C.text, lineHeight: 1.2 }}>
            {rows.length} waiting
          </div>
          <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, marginTop: 4, maxWidth: 760, lineHeight: 1.5 }}>
            Self-serve requests from the sign-up page. Each is a signal only — Sill has not
            turned the agency feature on and has not assigned a plan. Approve by opening the
            account and completing the normal flow (Agency Feature + Account → Plans). Dismiss
            to clear the row without acting.
          </div>
        </div>
      </div>

      {error !== null ? (
        <div style={{ fontFamily: sansFont, fontSize: 12, color: C.coralFg, marginBottom: 8 }}>
          {error}
        </div>
      ) : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {rows.map((r) => (
          <div
            key={r.account_id}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '8px 10px',
              background: C.surface2 ?? C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: 3,
              gap: 10,
              flexWrap: 'wrap',
            }}
          >
            <div style={{ minWidth: 0, flex: '1 1 320px' }}>
              <div style={{ fontFamily: sansFont, fontSize: 13, color: C.text, fontWeight: 600 }}>
                {r.account_name ?? r.account_id}
              </div>
              <div style={{ fontFamily: monoFont, fontSize: 10.5, color: C.muted, marginTop: 2 }}>
                {r.account_id}
                {r.owner_email !== null ? ` · ${r.owner_email}` : ''}
              </div>
              <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, marginTop: 2 }}>
                {r.plan_name} · {r.interval} · requested {formatIsoCompact(r.requested_at)}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <a
                href={`#/accounts/${encodeURIComponent(r.account_id)}`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  fontFamily: sansFont,
                  fontSize: 12,
                  padding: '6px 10px',
                  color: C.text,
                  background: 'transparent',
                  border: `1px solid ${C.border}`,
                  borderRadius: 3,
                  textDecoration: 'none',
                }}
              >
                Open account <ExternalLink size={11} />
              </a>
              <button
                type="button"
                onClick={() => void dismiss(r.account_id)}
                disabled={dismissing === r.account_id}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  fontFamily: sansFont,
                  fontSize: 12,
                  padding: '6px 10px',
                  color: C.muted,
                  background: 'transparent',
                  border: `1px solid ${C.border}`,
                  borderRadius: 3,
                  cursor: dismissing === r.account_id ? 'default' : 'pointer',
                  opacity: dismissing === r.account_id ? 0.6 : 1,
                }}
              >
                <X size={11} />
                {dismissing === r.account_id ? 'Dismissing…' : 'Dismiss'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
